import { useState, ReactNode, useMemo, useEffect } from "react";
import {
  ActivityFragment,
  EventFragment,
} from "../../../__generated__/graphql";
import { CalendarContext } from "./context";
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfQuarter,
  endOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  subMonths,
  subWeeks,
} from "date-fns";
import { Periodicity, PeriodInterval } from "./types";
import { formatDayOfWeek, formatMonth } from "../../../lib/utils";
import { ApolloError } from "@apollo/client";
import { useSearchParams } from "react-router";
import useActivityById from "../../../pages/projects/pages/project/components/Schedule/hooks/useActivityById";
import { ActivityDetailsModal } from "../../ActivityDetailsModal";

interface CalendarProviderProps {
  children: ReactNode;
  events: EventFragment[];
  activities?: ActivityFragment[];
  loading: boolean;
  error: ApolloError | undefined;
  onIntervalChange?: (interval: PeriodInterval) => void;
  defaultDate?: Date;
}

export function CalendarProvider({
  children,
  events,
  activities,
  loading,
  error,
  onIntervalChange,
  defaultDate,
}: CalendarProviderProps) {
  const [searchParams] = useSearchParams();
  // Use to focus on specific activity when coming from notification
  const activityId = searchParams.get("activityId");

  const { data: activityDetailsData } = useActivityById(activityId);

  const [periodicity, setPeriodicity] = useState<Periodicity>(
    Periodicity.MONTH
  );
  const [selectedEvent, setSelectedEvent] = useState<EventFragment | null>(
    null
  );
  const [currentDate, setCurrentDate] = useState(defaultDate || new Date());
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);

  useEffect(() => {
    if (activityId && activityDetailsData?.activity?.startDate) {
      setPeriodicity(Periodicity.WEEK);
      setCurrentDate(
        activityDetailsData?.activity?.startDate
          ? new Date(activityDetailsData?.activity?.startDate)
          : new Date()
      );
      setIsActivityModalOpen(true);
    }
  }, [activityId, activityDetailsData?.activity?.startDate]);

  const interval: PeriodInterval = useMemo(() => {
    let selectedInterval: PeriodInterval;
    switch (periodicity) {
      case Periodicity.QUARTER:
        selectedInterval = {
          start: startOfQuarter(currentDate),
          end: endOfQuarter(currentDate),
        };
        break;
      case Periodicity.MONTH: {
        const extendedFirstDay = startOfWeek(startOfMonth(currentDate), {
          weekStartsOn: 1,
        });

        const extendedLastDay = addDays(
          startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 }),
          41
        );

        selectedInterval = {
          start: extendedFirstDay,
          end: extendedLastDay,
        };
        break;
      }
      case Periodicity.WEEK:
        selectedInterval = {
          start: startOfWeek(currentDate, { weekStartsOn: 1 }),
          end: endOfWeek(currentDate, { weekStartsOn: 1 }),
        };
        break;
    }

    onIntervalChange?.(selectedInterval);
    return selectedInterval;
  }, [periodicity, currentDate, onIntervalChange]);

  const datesInPeriod = useMemo(() => {
    switch (periodicity) {
      case Periodicity.QUARTER: {
        const months = eachMonthOfInterval({
          start: interval.start,
          end: interval.end,
        });

        return months.map((month) => ({
          date: month,
          formatted: formatMonth(month),
        }));
      }
      case Periodicity.MONTH:
        return [
          {
            date: currentDate,
            formatted: formatMonth(currentDate),
          },
        ];
      case Periodicity.WEEK: {
        const days = eachDayOfInterval({
          start: interval.start,
          end: interval.end,
        });

        return days.map((day) => ({
          date: day,
          formatted: formatDayOfWeek(day),
        }));
      }
    }
  }, [periodicity, currentDate, interval]);

  const periodDescription = useMemo(() => {
    switch (periodicity) {
      case Periodicity.MONTH:
        return formatMonth(currentDate);
      case Periodicity.QUARTER:
        return datesInPeriod.map((date) => date.formatted).join(" - ");
      case Periodicity.WEEK:
        return formatMonth(currentDate);
    }
  }, [periodicity, currentDate, datesInPeriod]);

  const onPeriodChange = (direction: "left" | "right" | "today") => {
    if (direction === "today") {
      setCurrentDate(new Date());
      return;
    }

    switch (periodicity) {
      case Periodicity.MONTH:
        if (direction === "left") {
          setCurrentDate(subMonths(currentDate, 1));
        } else {
          setCurrentDate(addMonths(currentDate, 1));
        }
        break;
      case Periodicity.QUARTER:
        if (direction === "left") {
          setCurrentDate(subMonths(currentDate, 3));
        } else {
          setCurrentDate(addMonths(currentDate, 3));
        }
        break;
      case Periodicity.WEEK:
        if (direction === "left") {
          setCurrentDate(subWeeks(currentDate, 1));
        } else {
          setCurrentDate(addWeeks(currentDate, 1));
        }
        break;
    }
  };

  return (
    <CalendarContext.Provider
      value={{
        selectedEvent,
        setSelectedEvent,
        currentDate,
        events,
        activities,
        loading,
        error,
        periodicity,
        setPeriodicity,
        datesInPeriod,
        onPeriodChange,
        periodDescription,
      }}
    >
      {activityDetailsData?.activity ? (
        <ActivityDetailsModal
          activityId={activityDetailsData.activity.id}
          eventId={activityDetailsData.activity.eventId ?? null}
          isOpen={isActivityModalOpen}
          onClose={() => setIsActivityModalOpen(false)}
        />
      ) : null}

      {children}
    </CalendarContext.Provider>
  );
}
